import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Badge,
  Grid,
  Flex,
  Heading,
  useDisclosure,
} from '@chakra-ui/react';
import { FaArrowLeft, FaUser, FaExchangeAlt, FaPlus } from 'react-icons/fa';
import usePromise from '@hooks/usePromise';
import { formatDate } from '@utils/dateUtils';
import ContentLayout from '@layouts/ContentLayout';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { ALERT_STATUS_TYPE, ORDER_RESOURCE } from '@constants';
import { setAlert } from '@store/actions/alertActions';
import {
  CASE_INVESTIGATION_STATUS,
} from '@views/CaseInvestigationManager/constant';
import ModalLayout from '@components/Modal/ModalLayout';
import GenericSelectModal from '@components/Modal/GenericSelectModal';
import {
  caseInvestigationManagerConfig,
  investigatorNoteFieldConfig
} from '@views/CaseInvestigationManager/utils';


const CaseInvestigationDetailsView = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);

  const {
    isOpen: isStatusUpdateModalOpen,
    onOpen: onStatusUpdateModalOpen,
    onClose: onStatusUpdateModalClose
  } = useDisclosure();

  const [isModalLoading, setIsModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    investigatorNote: ''
  });

  const { fraudCaseDetails, loading, error } = useSelector((state) => ({
    fraudCaseDetails:
      state.caseInvestigationManager?.falcon?.caseInvestigationDetails?.data,
    loading:
      state.caseInvestigationManager?.falcon?.caseInvestigationDetails
        ?.isLoading,
    error:
      state.caseInvestigationManager?.falcon?.caseInvestigationDetails?.error
  }));

  const {
    run: fetchCaseDetails,
    isLoading: isFetching,
    error: fetchError
  } = usePromise();

  useEffect(() => {
    if (caseId) {
      fetchCaseDetails(
        dispatch(
          caseInvestigationManagerConfig.actions.getCaseInvestigationDetails({
            caseId
          })
        )
      );
    }
  }, [caseId, dispatch]);

  const handleBackClick = () => {
    navigate('/case-investigation-manager');
  };

  const handleUserDetailsClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  const handleOrderDetailsClick = (orderId) => {
    navigate(`/order/${orderId}?orderResource=${ORDER_RESOURCE.GOMS_SERVICE}`);
  };

  const handleFormDataChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddNote = () => {
    if (!formData.investigatorNote.trim()) return;

    const caseDetails = fraudCaseDetails?.caseDetails;
    const investigationCaseGuid = caseDetails?.investigationCaseGuid;

    if (!investigationCaseGuid) {
      dispatch(
        setAlert({
          title: 'Unable to add note: Case GUID not found',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    dispatch(
      caseInvestigationManagerConfig.actions.addCaseInvestigationNoteInvestigationByCaseId({
        caseGuid: investigationCaseGuid,
        payload: {
          commentText: formData.investigatorNote,
          linkedEntityType: 'CASE',
          linkedEntityGuid: investigationCaseGuid
        },
        onSuccess: () => {
          dispatch(
            setAlert({
              title: 'Note added successfully',
              status: ALERT_STATUS_TYPE.SUCCESS
            })
          );
          // Clear the form
          setFormData((prev) => ({
            ...prev,
            investigatorNote: ''
          }));
          // Refresh case details to get updated comments
          fetchCaseDetails(
            dispatch(
              caseInvestigationManagerConfig.actions.getCaseInvestigationDetails({
                caseId
              })
            )
          );
        },
        onComplete: () => {
          // Optional: handle completion
        }
      })
    );
  };

  const handleStatusUpdateSubmit = ({ value: selectedStatus }) => {
    setIsModalLoading(true);

    const caseDetails = fraudCaseDetails?.caseDetails;
    const investigationCaseGuid = caseDetails?.investigationCaseGuid;

    if (!investigationCaseGuid) {
      dispatch(
        setAlert({
          title: 'Unable to update status: Case GUID not found',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      setIsModalLoading(false);
      onStatusUpdateModalClose();
      return;
    }

    dispatch(
      caseInvestigationManagerConfig.actions.updateCaseInvestigationStatusByCaseId({
        caseGuid: investigationCaseGuid,
        status: selectedStatus,
        onSuccess: () => {
          // Refresh case details to get updated status
          fetchCaseDetails(
            dispatch(
              caseInvestigationManagerConfig.actions.getCaseInvestigationDetails({
                caseId
              })
            )
          );
        },
        onComplete: () => {
          setIsModalLoading(false);
          onStatusUpdateModalClose();
        }
      })
    );
  };

  const renderLoadingState = () => (
    <Box
      p={6}
      borderRadius="xl"
      w="full"
      bg="backGround"
      borderWidth="1px"
      borderColor="whiteAlpha.300"
    >
      <Flex justifyContent="center" alignItems="center" w="full" h="200px">
        <VStack spacing={4}>
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
          <Text color="whiteAlpha.600" fontSize="sm">
            Loading fraud case details...
          </Text>
        </VStack>
      </Flex>
    </Box>
  );

  const renderErrorState = () => (
    <Box
      p={6}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <VStack spacing={4} align="stretch">
        <Text color="red.500" fontSize="sm" fontWeight="medium">
          Error loading fraud case details
        </Text>
        <Text color="red.400" fontSize="xs">
          {fetchError?.message || error || 'Failed to load fraud case details'}
        </Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          onClick={() => window.location.reload()}
          alignSelf="flex-start"
        >
          Retry
        </Button>
      </VStack>
    </Box>
  );

  const renderNotFoundState = () => (
    <Box
      p={6}
      bg="backGround"
      border="1px solid"
      borderColor="yellow.300"
      borderRadius="xl"
    >
      <VStack spacing={4} align="stretch">
        <Text color="yellow.500" fontSize="sm" fontWeight="medium">
          No case found
        </Text>
        <Text color="yellow.400" fontSize="xs">
          The case with ID "{caseId}" could not be found.
        </Text>
        <Button
          size="sm"
          colorScheme="yellow"
          variant="outline"
          onClick={handleBackClick}
          alignSelf="flex-start"
        >
          Go Back
        </Button>
      </VStack>
    </Box>
  );

  const renderMainContent = () => {
    if (isFetching || loading) return renderLoadingState();
    if (fetchError || error) return renderErrorState();
    if (!fraudCaseDetails) return renderNotFoundState();

    const {
      caseDetails,
      linkedPersons,
      linkedOrders,
      comments,
      activityLog
    } = fraudCaseDetails;

    return (
      <VStack spacing={6} align="stretch">
        {/* Case Information */}
        <Card bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
          <CardHeader>
            <Text fontSize="lg" fontWeight="semibold" color="colorPrimary">
              Case Information
            </Text>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Classification:
                  </Text>
                  <Badge colorScheme="blue">
                    {caseDetails?.classification || 'N/A'}
                  </Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Trigger:
                  </Text>
                  <Text color="whiteAlpha.800">
                    {caseDetails?.triggerType || 'N/A'}
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Priority:
                  </Text>
                  <Badge colorScheme="red">{caseDetails?.priority || 'N/A'}</Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Status:
                  </Text>
                  <Badge colorScheme="green">{caseDetails?.status || 'N/A'}</Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Summary:
                  </Text>
                  <Text color="whiteAlpha.800">
                    {caseDetails?.summary || 'N/A'}
                  </Text>
                </HStack>
              </VStack>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Text fontWeight="medium" minW="140px" color="whiteAlpha.700">
                    Connected People:
                  </Text>
                  <Badge colorScheme="green">
                    {linkedPersons?.length || 0}
                  </Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="140px" color="whiteAlpha.700">
                    Connected Transaction:
                  </Text>
                  <Badge colorScheme="purple">
                    {linkedOrders?.length || 0}
                  </Badge>
                </HStack>
                {/*<HStack>*/}
                {/*  <Text fontWeight="medium" minW="140px" color="whiteAlpha.700">*/}
                {/*    Connected Beneficiary:*/}
                {/*  </Text>*/}
                {/*  <Badge colorScheme="orange">*/}
                {/*    {caseDetails?.connectedBeneficiaries?.length || 0}*/}
                {/*  </Badge>*/}
                {/*</HStack>*/}
                <HStack>
                  <Text fontWeight="medium" minW="140px" color="whiteAlpha.700">
                    Updated Date:
                  </Text>
                  <Text color="whiteAlpha.800">
                    {caseDetails?.updatedAt
                      ? formatDate(caseDetails.updatedAt)
                      : 'N/A'}
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Incident Date:
                  </Text>
                  <Text color="whiteAlpha.800">
                    {caseDetails?.incidentDate
                      ? new Date(caseDetails.incidentDate).toLocaleString()
                      : 'N/A'}
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px" color="whiteAlpha.700">
                    Created Date:
                  </Text>
                  <Text color="whiteAlpha.800">
                    {caseDetails?.createdAt
                      ? new Date(caseDetails.createdAt).toLocaleString()
                      : 'N/A'}
                  </Text>
                </HStack>
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Connected People */}
        <Card bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
          <CardHeader>
            <Text fontSize="lg" fontWeight="semibold" color="colorPrimary">
              Connected People
            </Text>
          </CardHeader>
          <CardBody>
            {linkedPersons?.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {linkedPersons.map((person, idx) => (
                  <Box
                    key={idx}
                    p={4}
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius="lg"
                    bg="whiteAlpha.100"
                    _hover={{
                      bg: 'whiteAlpha.200',
                      borderColor: 'whiteAlpha.600',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    transition="all 0.2s ease-in-out"
                  >
                    <HStack justify="space-between" align="center">
                      <HStack spacing={4}>
                        <Box
                          p={3}
                          borderRadius="full"
                          bg={
                            !person.isExternal
                              ? 'green.500'
                              : 'orange.500'
                          }
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <FaUser color="white" size="1rem" />
                        </Box>
                        <VStack align="start" spacing={1}>
                          <HStack spacing={2}>
                            <Text fontWeight="medium" color="whiteAlpha.800">
                              {person.isExternal ? person.fullName : person.userName || person.userEmail}
                            </Text>
                            <Badge
                              colorScheme={
                                !person.isExternal
                                  ? 'green'
                                  : 'orange'
                              }
                              fontSize="xs"
                            >
                              {person.isExternal ? 'EXTERNAL' : 'ASPORA'}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="whiteAlpha.600">
                            {person.isExternal ? person.email : person.userEmail} • {person.linkType}
                          </Text>
                          {!person.isExternal && person.userGuid && (
                            <HStack spacing={2}>
                              <Text
                                fontSize="xs"
                                color="green.300"
                                fontWeight="medium"
                              >
                                User ID: {person.userGuid}
                              </Text>
                            </HStack>
                          )}
                          {person.isExternal && person.userPhone && (
                            <Text fontSize="xs" color="whiteAlpha.500">
                              Contact: {person.userPhone}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                      {!person.isExternal && person.userGuid && (
                        <Button
                          as={RouterLink}
                          to={`/user/${person.userGuid}`}
                          colorScheme="brand"
                          size="sm"
                          color="black"
                          rightIcon={<FaArrowLeft style={{ transform: 'rotate(180deg)' }} />}
                        >
                          View Profile
                        </Button>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="whiteAlpha.500" textAlign="center" py={4}>
                No connected people found
              </Text>
            )}
          </CardBody>
        </Card>

        {/* Connected Transactions */}
        <Card bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
          <CardHeader>
            <Text fontSize="lg" fontWeight="semibold" color="colorPrimary">
              Connected Transactions
            </Text>
          </CardHeader>
          <CardBody>
            {linkedOrders?.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {linkedOrders.map((transaction, idx) => (
                  <Box
                    key={idx}
                    p={4}
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius="lg"
                    bg="whiteAlpha.100"
                    _hover={{
                      bg: 'whiteAlpha.200',
                      borderColor: 'whiteAlpha.600',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    transition="all 0.2s ease-in-out"
                    cursor="pointer"
                    onClick={() => handleOrderDetailsClick(transaction.orderGuid)}
                  >
                    <HStack justify="space-between" align="center">
                      <HStack spacing={4}>
                        <Box
                          p={3}
                          borderRadius="full"
                          bg="purple.500"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <FaExchangeAlt color="white" size="1rem" />
                        </Box>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium" color="whiteAlpha.800">
                            {transaction.orderGuid}
                          </Text>
                          {transaction.amount && transaction.currency && (
                            <Text fontSize="sm" color="whiteAlpha.600">
                              Amount: {transaction.amount} {transaction.currency}
                            </Text>
                          )}
                          <Text fontSize="xs" color="whiteAlpha.500">
                            Link Reason: {transaction.linkReason || 'N/A'}
                          </Text>
                          {transaction.status && (
                            <Text fontSize="xs" color="whiteAlpha.500">
                              Status: {transaction.status}
                            </Text>
                          )}
                          {transaction.orderDate && (
                            <Text fontSize="xs" color="whiteAlpha.500">
                              Order Date: {new Date(transaction.orderDate).toLocaleDateString()}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                      <Button
                        as={RouterLink}
                        to={`/order/${transaction.orderGuid}?orderResource=${ORDER_RESOURCE.GOMS_SERVICE}`}
                        colorScheme="brand"
                        size="sm"
                        color="black"
                        rightIcon={<FaArrowLeft style={{ transform: 'rotate(180deg)' }} />}
                      >
                        View Transaction
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="whiteAlpha.500" textAlign="center" py={4}>
                No connected transactions found
              </Text>
            )}
          </CardBody>
        </Card>

        {/* Investigator Notes */}
        <Card bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
          <CardHeader>
            <Text fontSize="lg" fontWeight="semibold" color="colorPrimary">
              Investigator Notes & History
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <VStack spacing={4} align="stretch">
                <Box color={'white'}>
                  <DynamicFormField
                    fieldConfig={investigatorNoteFieldConfig(formData)}
                    data={formData}
                    handleChange={handleFormDataChange}
                  />
                </Box>
                <HStack justify="flex-end">
                  <Button
                    colorScheme="brand"
                    size="sm"
                    color="black"
                    leftIcon={<FaPlus />}
                    onClick={handleAddNote}
                    isDisabled={!formData.investigatorNote.trim()}
                  >
                    Add Note
                  </Button>
                </HStack>
              </VStack>
              <VStack spacing={4} align="stretch">
                <Text color={'white'} fontSize={'md'}>
                  Investigation History
                </Text>
                {comments?.length > 0 ? (
                  <VStack spacing={3} align="stretch">
                    {comments.map((comment) => (
                      <Box
                        key={comment.commentGuid}
                        p={4}
                        bg="whiteAlpha.100"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="whiteAlpha.500"
                        position="relative"
                        _hover={{
                          bg: 'whiteAlpha.200',
                          borderColor: 'whiteAlpha.600',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                        transition="all 0.2s ease-in-out"
                      >
                        {comment.isEdited && (
                          <Badge
                            colorScheme="gray"
                            position="absolute"
                            top={2}
                            right={2}
                            fontSize="xs"
                          >
                            Edited
                          </Badge>
                        )}
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm" color="whiteAlpha.600">
                            {comment.createdBy?.name || 'Analyst'} • {formatDate(comment.createdAt)}
                          </Text>
                          <Text color="whiteAlpha.800">{comment.commentText}</Text>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text color="whiteAlpha.500" textAlign="center" py={4}>
                    No investigation notes yet
                  </Text>
                )}
              </VStack>
            </VStack>
          </CardBody>
        </Card>



        {/* Status Update Modal */}
        <ModalLayout
          isOpen={isStatusUpdateModalOpen}
          onClose={onStatusUpdateModalClose}
        >
          <GenericSelectModal
            title="Update Case Status"
            subtitle="Select the new status for this case investigation"
            label="Case Status"
            placeholder="Select status"
            options={Object.values(CASE_INVESTIGATION_STATUS)}
            initialValue={caseDetails?.status || ''}
            handleSubmit={handleStatusUpdateSubmit}
            isLoading={isModalLoading}
          />
        </ModalLayout>
      </VStack>
    );
  };

  return (
    <ContentLayout>
      <Grid gridTemplateColumns="1fr">
        {/* Main Column */}
        <VStack
          overflowY="auto"
          overflowX="hidden"
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems="stretch"
          gap={4}
        >
          <Flex justify="space-between" align="center">
            <Heading size="lg" color="colorPrimary">
              Case: {caseId}
            </Heading>
            {/* Action Button */}
            <HStack justify="center" pt={2} pb={4}>
              <Button
                colorScheme="brand"
                color={'black'}
                size="sm"
                borderRadius="md"
                onClick={onStatusUpdateModalOpen}
              >
                Update Case Status
              </Button>
            </HStack>
          </Flex>
          {renderMainContent()}
        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default CaseInvestigationDetailsView;
